// Tutorial page - simplified gameplay that doesn't require complex game hooks
import React, { useEffect } from 'react'
import { useGameStore } from '../store/gameStore'
import { TutorialProvider, useTutorial } from '../components/tutorial/TutorialProvider'
import TutorialOverlay from '../components/tutorial/TutorialOverlay'
import { TUTORIAL_INITIAL_STATE } from '../data/tutorialScenario'
import ArtistBoard from '../components/game/ArtistBoard'
import OpponentPanel from '../components/game/OpponentPanel'
import PlayerHand from '../components/game/PlayerHand'
import AuctionCenter from '../components/game/AuctionCenter'
import { Card as GameCardComponent } from '../components/Card'
import { getArtistIndex } from '../engine/constants'
import type { Card } from '../types'

/**
 * Simplified gameplay component for tutorial.
 * Unlike MainGameplay, this doesn't use complex hooks that depend on gameState presence.
 * It's a clean, simple layout that just renders the game UI elements.
 */
function TutorialGameplay() {
  const gameState = useGameStore((state) => state.gameState)
  const selectedCardId = useGameStore((state) => state.selectedCardId)

  if (!gameState) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading tutorial...
      </div>
    )
  }

  const currentPlayer = gameState.players[0]
  const { players, round } = gameState

  // Find selected card from hand
  const selectedCard = selectedCardId
    ? currentPlayer.hand.find((c) => c.id === selectedCardId) || null
    : null

  // No-op handlers - tutorial is observational
  const handleSelectCard = () => {}
  const handleExit = () => {
    window.location.href = '/'
  }
  const handlePlayCard = () => {}
  const handlePass = () => {}

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a1c20 0%, #2d3436 100%)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Background pattern */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          opacity: 0.02,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          pointerEvents: 'none',
        }}
      />

      {/* Tutorial Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 10,
        }}
      >
        {/* Left: Exit button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={handleExit}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
            }}
          >
            ✕
          </button>
          <span
            style={{
              fontSize: '18px',
              fontWeight: 300,
              color: 'white',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}
          >
            Tutorial
          </span>
        </div>

        {/* Center: Round indicator */}
        <div data-tutorial-id="round-indicator" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
            Round
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3, 4].map((r) => (
              <div
                key={r}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: r === round.roundNumber ? '#fbbf24' : 'rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: r === round.roundNumber ? 700 : 500,
                  color: r === round.roundNumber ? '#000' : 'rgba(255, 255, 255, 0.6)',
                }}
              >
                {r}
              </div>
            ))}
          </div>
        </div>

        <div style={{ width: '40px' }} /> {/* Spacer for centering */}
      </header>

      {/* Main content area */}
      <div
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '320px 1fr 280px',
          gap: '16px',
          padding: '16px',
          minHeight: 0,
          position: 'relative',
        }}
      >
        {/* Left column: Artist Board */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            overflowY: 'auto',
          }}
        >
          <ArtistBoard collapsed={false} />

          {/* Your Purchases */}
          <div
            style={{
              padding: '16px',
              background: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(12px)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                Your Purchases
              </span>
              <span
                style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.5)',
                }}
              >
                This Round
              </span>
            </div>

            {currentPlayer.purchasedThisRound.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                }}
              >
                {currentPlayer.purchasedThisRound.map((card: Card, idx: number) => (
                  <div
                    key={`${card.id}-${idx}`}
                    style={{ transform: 'scale(0.6)' }}
                  >
                    <GameCardComponent
                      card={{
                        id: card.id,
                        artist: card.artist,
                        artistIndex: getArtistIndex(card.artist),
                        cardIndex: card.cardIndex,
                        auctionType: card.auctionType
                      }}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontStyle: 'italic',
                }}
              >
                No purchases yet
              </div>
            )}
          </div>
        </div>

        {/* Center column: Auction Area */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginTop: '0px',
          }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '650px',
            }}
          >
            <AuctionCenter
              selectedCard={selectedCard}
              isPlayerTurn={round.phase.type === 'awaiting_card_play' && round.phase.activePlayerIndex === 0}
              onPlayCard={handlePlayCard}
              onPass={handlePass}
            />
          </div>
        </div>

        {/* Right column: Opponents */}
        <div data-tutorial-id="opponents-panel" style={{ overflowY: 'auto' }}>
          <div
            style={{
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Opponents
          </div>
          <OpponentPanel
            players={players}
            currentPlayerIndex={0}
            activePlayerIndex={
              round.phase.type === 'awaiting_card_play'
                ? round.phase.activePlayerIndex
                : round.currentAuctioneerIndex
            }
          />
        </div>
      </div>

      {/* Bottom: Player Hand */}
      <PlayerHand
        cards={currentPlayer.hand}
        selectedCardId={null}
        onSelectCard={handleSelectCard}
        money={currentPlayer.money}
        disabled={false} // Enable so cards aren't greyed out
        purchasedThisRound={currentPlayer.purchasedThisRound}
      />
    </div>
  )
}

/**
 * Tutorial page component.
 *
 * Approach:
 * 1. Load TUTORIAL_INITIAL_STATE into the real gameStore on mount
 * 2. Render TutorialGameplay (simplified version of MainGameplay)
 * 3. Add TutorialOverlay on top for highlights and tooltips
 * 4. TutorialProvider manages step progression (not gameState)
 * 5. Update gameStore when tutorial step changes
 */
const TutorialPage: React.FC = () => {
  // Load tutorial scenario into gameStore on mount
  useEffect(() => {
    console.log('[TutorialPage] Loading tutorial scenario into gameStore')
    useGameStore.setState(
      {
        gameState: TUTORIAL_INITIAL_STATE,
        isGameStarted: true,
        selectedCardId: null,
        firstPlayerIndex: 0,
      },
      false,
      'loadTutorialScenario'
    )

    // Cleanup: reset game state when leaving tutorial
    return () => {
      console.log('[TutorialPage] Cleaning up tutorial state')
      useGameStore.setState({ gameState: null, isGameStarted: false }, false, 'exitTutorial')
    }
  }, []) // Empty dependency array - only run on mount

  // Component to sync game state with tutorial steps
  const TutorialGameStateSync: React.FC = () => {
    const { currentStepData } = useTutorial()

    useEffect(() => {
      console.log('[TutorialPage] Updating game state for step', currentStepData.id)
      useGameStore.setState(
        {
          gameState: currentStepData.gameState,
          isGameStarted: true,
          selectedCardId: currentStepData.selectedCardId ?? null,
          firstPlayerIndex: 0,
        },
        false,
        'tutorialStepChange'
      )
    }, [currentStepData.id, currentStepData.gameState, currentStepData.selectedCardId])

    return null
  }

  return (
    <TutorialProvider>
      <TutorialGameStateSync />
      <TutorialGameplay />
      <TutorialOverlay />
    </TutorialProvider>
  )
}

export default TutorialPage
